import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';

import AbandonedCall from 'models/abandoned_call';
import ChatCustomerUrl from 'models/chat_customer_url';
import ChatMessage from 'models/chat_message';
import ConversationItem from 'models/conversation_item';
import ConversationMessage from 'models/conversation_message';
import ConversationNote from 'models/conversation_note';
import CustomerActivity from 'models/customer_activity';
import EmailPreviewModel from 'models/content_preview/email_preview';
import EmailMessage from 'models/email_message';
import EmailSendFailure from 'models/email_send_failure';
import getClassName from 'scripts/lib/class_name';
import FbMessageIncoming from 'models/fb_message_incoming';
import FbMessageOutgoing from 'models/fb_message_outgoing';
import HotelReservation from 'models/hotel_reservation';
import htmlToText from 'components/lib/view_helpers';
import ItemLinkContent, { createConversationItemFromItemLink } from 'models/item_link_content';
import LongDate from 'components/lib/date/long_date';
import PhoneCall from 'models/phone_call';
import PhoneCallback from 'models/phone_callback';
import PhoneDuration from 'components/lib/phone_duration';
import ReportDuration from 'components/reporting/report/lib/report_duration';
import ShortTime from 'components/lib/date/short_time';
import SmsMessage from 'models/sms_message';
import Task from 'models/task';
import TaskCompletionContent from 'models/task_completion_content';
import TaskReopenedContent from 'models/task_reopened_content';
import Tweet from 'models/tweet';
import SecureDataRequest from 'models/secure_data_request';
import Voicemail from 'models/voicemail';
import WebForm from 'models/web_form';

import { formatPhoneNumber } from 'models/phone_number';

const ConversationItemPreviewSeperator = '\xa0-\xa0';

function ConversationItemPreview({ conversationItem, hasTopic }) {
  if (!conversationItem) {
    return null;
  }

  let content = conversationItem.content;

  if (content instanceof AbandonedCall) {
    return <AbandonedCallPreview content={content} />;
  } else if (content instanceof ConversationNote) {
    return <ConversationNotePreview content={content} />;
  } else if (content instanceof ChatMessage) {
    return <ChatMessagePreview content={content} />;
  } else if (content instanceof ChatCustomerUrl) {
    return <ChatCustomerUrlPreview content={content} />;
  } else if (content instanceof ConversationMessage) {
    return <ConversationMessagePreview content={content} />;
  } else if (content instanceof CustomerActivity) {
    return <CustomerActivityPreview content={content} />;
  } else if (content instanceof EmailMessage) {
    return <EmailMessagePreview content={content} />;
  } else if (content instanceof EmailPreviewModel) {
    return <EmailPreview content={content} />;
  } else if (content instanceof EmailSendFailure) {
    return <EmailSendFailurePreview content={content} />;
  } else if (content instanceof FbMessageIncoming) {
    return <FbMessageIncomingPreview content={content} />;
  } else if (content instanceof FbMessageOutgoing) {
    return <FbMessageOutgoingPreview content={content} />;
  } else if (content instanceof HotelReservation) {
    return <HotelReservationPreview content={content} />;
  } else if (content instanceof ItemLinkContent) {
    return <ItemLinkPreview conversationItem={conversationItem} />;
  } else if (content instanceof PhoneCall) {
    return <PhoneCallPreview content={content} initiatorType={conversationItem.initiator.type} />;
  } else if (content instanceof PhoneCallback) {
    return <PhoneCallbackPreview content={content} />;
  } else if (content instanceof SmsMessage) {
    return <SmsMessagePreview content={content} />;
  } else if (content instanceof Task) {
    return <TaskPreview content={content} />;
  } else if (content instanceof TaskCompletionContent) {
    return <TaskCompletionPreview content={content} hasTopic={hasTopic} />;
  } else if (content instanceof TaskReopenedContent) {
    return <TaskReopenedPreview content={content} hasTopic={hasTopic} />;
  } else if (content instanceof Tweet) {
    return <TweetPreview content={content} />;
  } else if (content instanceof SecureDataRequest) {
    return <SecureDataRequestPreview content={content} />;
  } else if (content instanceof Voicemail) {
    return <VoicemailPreview content={content} />;
  } else if (content instanceof WebForm) {
    return <WebFormPreview content={content} />;
  } else if (content === null) {
    return <div className="preview">New Conversation</div>;
  }

  throw new Error(`could not render item preview from [${getClassName(content)}]`);
}

ConversationItemPreview.propTypes = {
  conversationItem: PropTypes.instanceOf(ConversationItem),
  hasTopic: PropTypes.bool,
};

export default ConversationItemPreview;

function AbandonedCallPreview({ content }) {
  return <div className="preview abandonedCallPreview">Call from {formatPhoneNumber(content.customerNumber)}</div>;
}

AbandonedCallPreview.propTypes = { content: PropTypes.instanceOf(AbandonedCall) };

function ChatCustomerUrlPreview({ content }) {
  return <div className="preview chatCustomerUrlPreview">{content.customerUrl}</div>;
}

ChatCustomerUrlPreview.propTypes = { content: PropTypes.instanceOf(ChatCustomerUrl) };

function ChatMessagePreview({ content }) {
  return <div className="preview chatMessagePreview">{content.text}</div>;
}

ChatMessagePreview.propTypes = { content: PropTypes.instanceOf(ChatMessage) };

function ConversationMessagePreview({ content }) {
  return <div className="preview conversationMessagePreview">{content.getBody()}</div>;
}

ConversationMessagePreview.propTypes = { content: PropTypes.instanceOf(ConversationMessage) };

function ConversationNotePreview({ content }) {
  return <div className="preview conversationNotePreview">{htmlToText(content.content)}</div>;
}

ConversationNotePreview.propTypes = { content: PropTypes.instanceOf(ConversationNote) };

function CustomerActivityPreview({ content }) {
  let activityContent = content.title || content.body;
  return <div className="preview customerActivityPreview">{activityContent}</div>;
}

function EmailMessagePreview({ content }) {
  let emailSubject = content.subject ? `${content.subject}${ConversationItemPreviewSeperator}` : '';
  return <div className="preview emailMessagePreview">{`${emailSubject}${content.getNonQuotedPlainText()}`}</div>;
}

EmailMessagePreview.propTypes = { content: PropTypes.instanceOf(EmailMessage) };

function EmailPreview({ content }) {
  let emailSubject = content.subject ? `${content.subject}${ConversationItemPreviewSeperator}` : '';
  return <div className="preview emailPreview">{`${emailSubject}${content.excerpt}`}</div>;
}

EmailPreview.propTypes = { content: PropTypes.instanceOf(EmailPreviewModel) };

function EmailSendFailurePreview({ content }) {
  return (
    <div className="preview preview-emailSendFailure">
      {content.recipient ? `Email not delivered for ${content.recipient}.` : 'Email not delivered.'}
    </div>
  );
}

EmailSendFailurePreview.propTypes = { content: PropTypes.instanceOf(EmailSendFailure) };

function FbMessageIncomingPreview({ content }) {
  return <div className="preview fbMessageIncomingPreview">{content.message.text}</div>;
}

FbMessageIncomingPreview.propTypes = { content: PropTypes.instanceOf(FbMessageIncoming) };

function FbMessageOutgoingPreview({ content }) {
  return <div className="preview fbMessageOutgoingPreview">{content.request.message.text}</div>;
}

FbMessageOutgoingPreview.propTypes = { content: PropTypes.instanceOf(FbMessageOutgoing) };

function HotelReservationPreview({ content }) {
  return (
    <div className="preview hotelReservationPreview">
      {content.hotel} on <LongDate timestamp={content.timestamp} /> at <ShortTime timestamp={content.timestamp} />
    </div>
  );
}

HotelReservationPreview.propTypes = { content: PropTypes.instanceOf(HotelReservation) };

function ItemLinkPreview({ conversationItem }) {
  let originalItem = createConversationItemFromItemLink(conversationItem);
  return <ConversationItemPreview conversationItem={originalItem} />;
}

ItemLinkPreview.propTypes = { conversationItem: PropTypes.instanceOf(ConversationItem) };

function PhoneCallPreview({ content, initiatorType }) {
  return (
    <div className="preview phoneCallPreview">
      <PhoneDuration content={content} initiatorType={initiatorType} />
    </div>
  );
}

PhoneCallPreview.propTypes = { content: PropTypes.instanceOf(PhoneCall), initiatorType: PropTypes.string };

function PhoneCallbackPreview({ content }) {
  return (
    <div className="preview phoneCallbackPreview">
      Scheduled a call back at <ShortTime timestamp={content.requestedTime} />
    </div>
  );
}

PhoneCallbackPreview.propTypes = { content: PropTypes.instanceOf(PhoneCallback) };

function SmsMessagePreview({ content }) {
  return <div className="preview smsPreview">{content.body}</div>;
}

SmsMessagePreview.propTypes = { content: PropTypes.instanceOf(SmsMessage) };

function TaskPreview({ content }) {
  return <div className="preview taskPreview">{htmlToText(content.note)}</div>;
}

TaskPreview.propTypes = { content: PropTypes.instanceOf(Task) };

function TaskCompletionPreview({ content, hasTopic }) {
  return (
    <div className="preview taskCompletionPreview">
      {hasTopic ? 'Closed Task - ' : ''}
      {htmlToText(content.note)}
    </div>
  );
}

TaskCompletionPreview.propTypes = {
  content: PropTypes.instanceOf(TaskCompletionContent),
  hasTopic: PropTypes.bool,
};

function TaskReopenedPreview({ content, hasTopic }) {
  return (
    <div className="preview taskReopenedPreview">
      {hasTopic ? 'Task Reopened - ' : ''}
      {htmlToText(content.note)}
    </div>
  );
}

TaskReopenedPreview.propTypes = {
  content: PropTypes.instanceOf(TaskReopenedContent),
  hasTopic: PropTypes.bool,
};

function TweetPreview({ content }) {
  return <div className="preview tweetPreview">{content.text}</div>;
}

TweetPreview.propTypes = { content: PropTypes.instanceOf(Tweet) };

function VoicemailPreview({ content }) {
  return (
    <div className="preview voicemailPreview">
      Voicemail from {formatPhoneNumber(content.customerNumber)}
      {content.recordingDuration && ConversationItemPreviewSeperator}
      {content.recordingDuration && <ReportDuration duration={content.recordingDuration * 1000} />}
    </div>
  );
}

SecureDataRequestPreview.propTypes = { content: PropTypes.instanceOf(SecureDataRequest) };

function SecureDataRequestPreview({ content }) {
  return <div className="preview secureDataRequestPreview">{content.metadata.memo}</div>;
}

VoicemailPreview.propTypes = { content: PropTypes.instanceOf(Voicemail) };

function WebFormPreview({ content }) {
  return <div className="preview webFormPreview">{_.values(content.content).join(' ')}</div>;
}

WebFormPreview.propTypes = { content: PropTypes.instanceOf(WebForm) };



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/lib/conversation_item_preview.jsx